import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import {
  ChevronDown,
  Command,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Sparkles,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../lib/auth-context";
import { groupLabels, modules } from "../lib/modules";
import { maiaEnvironment } from "../lib/maia-environment";
import { Input, Modal } from "./ui";
import styles from "./app-shell.module.css";

const sectionRoutes = [
  { href: "/dashboard", label: "Обзор" },
  { href: "/chat", label: "Внутренний чат", module: "maia-chat" },
  { href: "/users", label: "Пользователи", module: "users" },
];

export function AppShell() {
  const { user, loading, logout, can } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("shell.sidebar.collapsed") === "1",
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState("");

  useEffect(
    () =>
      localStorage.setItem("shell.sidebar.collapsed", collapsed ? "1" : "0"),
    [collapsed],
  );

  useEffect(() => {
    const openCommand = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen(true);
      }
    };
    window.addEventListener("keydown", openCommand);
    return () => window.removeEventListener("keydown", openCommand);
  }, []);

  const visibleModules = useMemo(
    () => modules.filter((item) => can(item.id)),
    [can],
  );
  const commandItems = useMemo(() => {
    const items = [
      ...sectionRoutes
        .filter((item) => !item.module || can(item.module))
        .map((item) => ({
          href: item.href,
          title: item.label,
          description: undefined as string | undefined,
        })),
      ...visibleModules.map((item) => ({
        href: item.href,
        title: item.title,
        description: item.description,
      })),
    ];
    return Array.from(
      new Map(items.map((item) => [item.href, item])).values(),
    );
  }, [can, visibleModules]);
  const normalizedQuery = commandQuery.trim().toLocaleLowerCase("ru");
  const filteredCommandItems = normalizedQuery
    ? commandItems.filter((item) =>
        `${item.title} ${item.description || ""}`
          .toLocaleLowerCase("ru")
          .includes(normalizedQuery),
      )
    : commandItems;

  if (loading)
    return (
      <div className={styles.boot}>
        <Sparkles size={20} />
        <span>Загрузка рабочей области</span>
      </div>
    );
  if (!user) {
    void navigate({ to: "/", replace: true });
    return null;
  }

  const handleLogout = () => {
    logout();
    void navigate({ to: "/", replace: true });
  };

  return (
    <div className={`${styles.shell} ${collapsed ? styles.collapsed : ""}`}>
      <header className={styles.topbar}>
        <button
          className={styles.mobileMenu}
          onClick={() => setMobileOpen(true)}
          aria-label="Открыть меню"
        >
          <Menu size={18} />
        </button>
        <Link to="/dashboard" className={styles.brand}>
          <span className={styles.brandMark}>M</span>
          <span>
            MAIA{import.meta.env.VITE_DEPLOY_ENV === "development" && " · DEV"}
          </span>
          {import.meta.env.VITE_DEPLOY_ENV === "development" && (
            <small title={import.meta.env.VITE_RELEASE_SHA}>
              {import.meta.env.VITE_RELEASE_SHA?.slice(0, 8)}
            </small>
          )}
        </Link>
        <a className={styles.environmentLink}
          href={maiaEnvironment() === "dev" ? "https://lab.klientiks.ru" : "https://dev.lab.klientiks.ru"}
          title="Откроется отдельная админка. Несохранённые изменения не переносятся."
          target="_blank" rel="noreferrer">
          {maiaEnvironment().toUpperCase()} · Открыть {maiaEnvironment() === "dev" ? "PROD" : "DEV"}
        </a>
        <button
          className={styles.command}
          type="button"
          onClick={() => setCommandOpen(true)}
        >
          <Command size={14} />
          <span>Перейти к разделу</span>
          <kbd>Ctrl K</kbd>
        </button>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger className={styles.userTrigger}>
            <span className={styles.avatar}>
              {(user.display_name || user.username).slice(0, 1).toUpperCase()}
            </span>
            <span className={styles.userText}>
              {user.display_name || user.username}
            </span>
            <ChevronDown size={14} />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className={styles.userMenu}
              align="end"
              sideOffset={6}
            >
              <DropdownMenu.Label className={styles.menuLabel}>
                {user.username}
              </DropdownMenu.Label>
              <DropdownMenu.Item
                className={styles.menuItem}
                onSelect={() => void navigate({ to: "/account" })}
              >
                <UserRound size={15} />
                Профиль
              </DropdownMenu.Item>
              <DropdownMenu.Separator className={styles.menuSeparator} />
              <DropdownMenu.Item
                className={styles.menuItem}
                onSelect={handleLogout}
              >
                <LogOut size={15} />
                Выйти
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </header>

      <aside
        className={`${styles.sidebar} ${mobileOpen ? styles.mobileOpen : ""}`}
      >
        <div className={styles.mobileSidebarHeader}>
          <span>Навигация</span>
          <button
            onClick={() => setMobileOpen(false)}
            aria-label="Закрыть меню"
          >
            <X size={18} />
          </button>
        </div>
        <nav className={styles.nav} aria-label="Основная навигация">
          <div className={styles.navGroup}>
            {sectionRoutes
              .filter((item) => !item.module || can(item.module))
              .map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={styles.navItem}
                  activeProps={{ className: styles.active }}
                >
                  <span className={styles.navGlyph}>
                    {item.label.slice(0, 1)}
                  </span>
                  <span>{item.label}</span>
                </Link>
              ))}
          </div>
          {(["ai", "content", "operations"] as const).map((group) => {
            const items = visibleModules.filter(
              (item) =>
                item.group === group &&
                !sectionRoutes.some((route) => route.module === item.id),
            );
            if (!items.length) return null;
            return (
              <div className={styles.navGroup} key={group}>
                <p className={styles.groupLabel}>{groupLabels[group]}</p>
                {items.map((item) => {
                  const Icon = item.icon;
                  return item.href.startsWith("http") ? (
                    <a
                      key={item.id}
                      href={item.href}
                      onClick={() => setMobileOpen(false)}
                      className={styles.navItem}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon size={16} />
                      <span>{item.title}</span>
                    </a>
                  ) : (
                    <Link
                      key={item.id}
                      to={item.href}
                      onClick={() => setMobileOpen(false)}
                      className={styles.navItem}
                      activeProps={{ className: styles.active }}
                    >
                      <Icon size={16} />
                      <span>{item.title}</span>
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className={styles.sidebarFooter}>
          <Link
            to="/account"
            className={styles.navItem}
            activeProps={{ className: styles.active }}
          >
            <Settings size={16} />
            <span>Настройки профиля</span>
          </Link>
          <button
            className={styles.collapseButton}
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? "Развернуть меню" : "Свернуть меню"}
          >
            {collapsed ? (
              <PanelLeftOpen size={16} />
            ) : (
              <PanelLeftClose size={16} />
            )}
            <span>Свернуть</span>
          </button>
        </div>
      </aside>
      {mobileOpen ? (
        <button
          className={styles.mobileOverlay}
          onClick={() => setMobileOpen(false)}
          aria-label="Закрыть меню"
        />
      ) : null}

      <main className={styles.content}>
        <div className={styles.contentInner}>
          <Outlet />
        </div>
      </main>
      <Modal
        open={commandOpen}
        onOpenChange={(open) => {
          setCommandOpen(open);
          if (!open) setCommandQuery("");
        }}
        title="Перейти к разделу"
        description="Найдите сервис или рабочую область."
      >
        <div className={styles.commandPalette}>
          <div className={styles.commandSearch}>
            <Command size={16} />
            <Input
              autoFocus
              placeholder="Поиск раздела"
              value={commandQuery}
              onChange={(event) => setCommandQuery(event.target.value)}
            />
          </div>
          <div className={styles.commandResults}>
            {filteredCommandItems.length ? (
              filteredCommandItems.map((item) =>
                item.href.startsWith("http") ? (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setCommandOpen(false)}
                  >
                    <strong>{item.title}</strong>
                    {item.description ? <span>{item.description}</span> : null}
                  </a>
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setCommandOpen(false)}
                  >
                    <strong>{item.title}</strong>
                    {item.description ? <span>{item.description}</span> : null}
                  </Link>
                ),
              )
            ) : (
              <p className={styles.commandEmpty}>Разделы не найдены</p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
